import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import { Readout, AirFontImport, apiErrorMessage } from "./airTheme";

// ── Constants ─────────────────────────────────────────────────────
const PAX_LABELS = { 0: "Adult", 1: "Child (2–11)", 2: "Infant (<2)" };
const PAX_TITLES = {
  0: ["MR", "MRS", "MS", "DR"],
  1: ["MSTR", "MISS"],
  2: ["MSTR", "MISS"],
};

// ── Helpers ───────────────────────────────────────────────────────
const toDobApi = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${m}/${d}/${y}`;
};
const fmtTime = (dt) => (dt ? dt.slice(11, 16) || dt : "--");
const fmtDate = (dt) => {
  if (!dt) return "--";
  const d = new Date(dt);
  return isNaN(d) ? dt : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ── Design tokens ─────────────────────────────────────────────────
const C = {
  navy:   "#0B1A35",
  navyMd: "#162C5C",
  sky:    "#0EA5E9",
  bg:     "#F0F4FA",
  card:   "#FFFFFF",
  border: "#E2EAF4",
  muted:  "#64748B",
  label:  "#94A3B8",
  green:  "#10B981",
  amber:  "#F59E0B",
  red:    "#EF4444",
};

// ── SVG icon system ───────────────────────────────────────────────
const Icon = ({ d, size = 16, fill = "none", strokeWidth = "2", ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
    {d}
  </svg>
);

const IcArrowLeft   = (p) => <Icon size={p.size} d={<><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></>} />;
const IcPhone       = (p) => <Icon size={p.size} d={<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.5a19.79 19.79 0 01-3-8.57A2 2 0 012.18 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.16a16 16 0 006 6l1.52-1.52a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>} />;
const IcMail        = (p) => <Icon size={p.size} d={<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>} />;
const IcUsers       = (p) => <Icon size={p.size} d={<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></>} />;
const IcUser        = (p) => <Icon size={p.size} d={<><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></>} />;
const IcBriefcase   = (p) => <Icon size={p.size} d={<><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></>} />;
const IcCheck       = (p) => <Icon size={p.size || 11} strokeWidth={p.strokeWidth || "2.5"} d={<polyline points="20 6 9 17 4 12"/>} />;
const IcAlertTri    = (p) => <Icon size={p.size} d={<><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="3"/></>} />;
const IcXCircle     = (p) => <Icon size={p.size} d={<><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>} />;

// Filled airplane silhouette — points right, used on route line
const IcAirplaneFill = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
  </svg>
);

// ── Input styles ──────────────────────────────────────────────────
const iStyle = (focused) => ({
  width: "100%", height: 42, borderRadius: 10,
  border: `1.5px solid ${focused ? C.sky : C.border}`,
  padding: "0 12px", fontSize: 13, color: "#111827", outline: "none",
  boxSizing: "border-box", background: "#fff", fontFamily: "inherit",
  boxShadow: focused ? "0 0 0 3px rgba(14,165,233,0.12)" : "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
});

const Label = ({ children, required }) => (
  <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: C.label, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.08em" }}>
    {children}{required && <span style={{ color: C.red, marginLeft: 2 }}>*</span>}
  </label>
);

const Field = ({ label, value, onChange, type = "text", placeholder, required }) => {
  const [f, setF] = useState(false);
  return (
    <div>
      <Label required={required}>{label}</Label>
      <input type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setF(true)} onBlur={() => setF(false)}
        style={iStyle(f)} />
    </div>
  );
};

const Select = ({ label, value, onChange, options, required }) => (
  <div>
    <Label required={required}>{label}</Label>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      style={{ ...iStyle(false), cursor: "pointer" }}>
      <option value="">Select</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const DateInput = ({ label, value, onChange, required }) => {
  const [f, setF] = useState(false);
  return (
    <div>
      <Label required={required}>{label}</Label>
      <input type="date" value={value} onChange={onChange}
        onFocus={() => setF(true)} onBlur={() => setF(false)}
        style={iStyle(f)} />
    </div>
  );
};

// ── Boarding-pass flight card ──────────────────────────────────────
const FlightCard = ({ flight, label }) => {
  const segs  = flight.Segments || [];
  const first = segs[0];
  const last  = segs[segs.length - 1];
  const fare  = (flight.Fares || [])[0];
  const total = (fare?.FareDetails || []).reduce((s, fd) => s + (fd.Total_Amount || 0), 0);
  const stops = segs.length - 1;

  return (
    <div style={{ borderRadius: 20, overflow: "hidden", marginBottom: 14, boxShadow: "0 10px 32px rgba(11,26,53,0.22)" }}>

      {/* ── Main body ── */}
      <div style={{ background: `linear-gradient(138deg, ${C.navy} 0%, ${C.navyMd} 100%)`, padding: "20px 22px 18px", position: "relative", overflow: "hidden" }}>
        {/* Subtle diagonal texture */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.012) 0, rgba(255,255,255,0.012) 1px, transparent 1px, transparent 22px)", pointerEvents: "none" }} />

        {/* Top: eyebrow + airline + badge */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ color: "rgba(255,255,255,0.28)", fontSize: 9, fontWeight: 800, letterSpacing: "0.22em", marginBottom: 5 }}>BOARDING PASS</div>
            <div style={{ color: "rgba(255,255,255,0.88)", fontWeight: 700, fontSize: 13, lineHeight: 1.3 }}>{first?.Airline_Name || flight.Airline_Code}</div>
            <div style={{ marginTop: 2 }}><Readout size={11} weight={500} color="rgba(255,255,255,0.35)">{flight.Flight_Numbers}</Readout> <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 11 }}>· Economy</span></div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {label && (
              <span style={{ background: C.sky, color: "#fff", fontSize: 9, fontWeight: 800, padding: "3px 10px", borderRadius: 20, letterSpacing: "0.08em" }}>
                {label.toUpperCase()}
              </span>
            )}
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.4)" }}>
              <IcAirplaneFill size={18} color="rgba(255,255,255,0.4)" />
            </div>
          </div>
        </div>

        {/* Route */}
        <div style={{ display: "flex", alignItems: "center" }}>
          {/* Origin */}
          <div>
            <span className="air-route-code"><Readout size={40} weight={700} color="#fff" tracking="-0.02em" style={{ lineHeight: 1 }}>{first?.Origin}</Readout></span>
            <div style={{ marginTop: 6 }}><Readout size={22} weight={700} color={C.sky} tracking="0.04em">{fmtTime(first?.Departure_DateTime)}</Readout></div>
            <div style={{ color: "rgba(255,255,255,0.28)", fontSize: 10, marginTop: 4, letterSpacing: "0.03em" }}>{fmtDate(first?.Departure_DateTime)}</div>
          </div>

          {/* Route line */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 7, padding: "0 14px" }}>
            <span style={{ fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,0.28)", letterSpacing: "0.16em" }}>
              {stops === 0 ? "DIRECT" : `${stops} STOP${stops > 1 ? "S" : ""}`}
            </span>
            <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, height: 1, backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.2) 0, rgba(255,255,255,0.2) 4px, transparent 4px, transparent 9px)" }} />
              <IcAirplaneFill size={15} color={C.sky} />
              <div style={{ flex: 1, height: 1, backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.2) 0, rgba(255,255,255,0.2) 4px, transparent 4px, transparent 9px)" }} />
            </div>
          </div>

          {/* Destination */}
          <div style={{ textAlign: "right" }}>
            <span className="air-route-code"><Readout size={40} weight={700} color="#fff" tracking="-0.02em" style={{ lineHeight: 1 }}>{last?.Destination}</Readout></span>
            <div style={{ marginTop: 6 }}><Readout size={22} weight={700} color={C.sky} tracking="0.04em">{fmtTime(last?.Arrival_DateTime)}</Readout></div>
            <div style={{ color: "rgba(255,255,255,0.28)", fontSize: 10, marginTop: 4, letterSpacing: "0.03em" }}>{fmtDate(last?.Arrival_DateTime)}</div>
          </div>
        </div>
      </div>

      {/* ── Perforated tear-line ── */}
      <div style={{ background: C.navyMd, display: "flex", alignItems: "center", height: 20 }}>
        <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.bg, marginLeft: -9, flexShrink: 0 }} />
        <div style={{ flex: 1, borderTop: "1.5px dashed rgba(255,255,255,0.1)" }} />
        <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.bg, marginRight: -9, flexShrink: 0 }} />
      </div>

      {/* ── Ticket stub footer ── */}
      <div style={{ background: C.navyMd, padding: "14px 22px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ color: "rgba(255,255,255,0.28)", fontSize: 9, fontWeight: 800, letterSpacing: "0.14em", marginBottom: 5 }}>FARE PER PASSENGER</div>
          <Readout size={22} weight={700} color="#fff" tracking="-0.01em">₹{total.toLocaleString("en-IN")}</Readout>
        </div>
        {flight.IsFareChange && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 8, padding: "7px 11px", color: "#FCD34D" }}>
            <IcAlertTri size={13} />
            <span style={{ fontSize: 11, fontWeight: 700 }}>Fare updated</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Section card ──────────────────────────────────────────────────
const Card = ({ icon, title, children }) => (
  <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: "20px", marginBottom: 12 }}>
    {(icon || title) && (
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
        {icon && (
          <div style={{ width: 32, height: 32, borderRadius: 10, background: "#EFF8FF", display: "flex", alignItems: "center", justifyContent: "center", color: C.sky, flexShrink: 0 }}>
            {icon}
          </div>
        )}
        {title && <span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{title}</span>}
      </div>
    )}
    {children}
  </div>
);

// ── Passenger form card ───────────────────────────────────────────
const PaxCard = ({ pax, idx, onChange }) => {
  const titles = PAX_TITLES[pax.paxType] || ["MR", "MRS", "MS"];

  return (
    <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 12, overflow: "hidden" }}>
      <div style={{ padding: "13px 20px", borderBottom: `1px solid ${C.border}`, background: "#FAFCFF", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: C.navy, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
          <IcUser size={14} />
        </div>
        <span style={{ fontWeight: 700, fontSize: 13, color: C.navy }}>Passenger {idx + 1}</span>
        <span style={{ background: "#EFF8FF", color: C.sky, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, letterSpacing: "0.04em" }}>
          {PAX_LABELS[pax.paxType].toUpperCase()}
        </span>
      </div>

      <div style={{ padding: "18px 20px" }}>
        <div className="air-grid-3a" style={{ display: "grid", gridTemplateColumns: "90px minmax(0,1fr) minmax(0,1fr)", gap: 12, marginBottom: 14 }}>
          <Select label="Title" value={pax.title} onChange={(v) => onChange(idx, "title", v)} options={titles} required />
          <Field label="First Name" value={pax.firstName} onChange={(v) => onChange(idx, "firstName", v)} placeholder="As on ID" required />
          <Field label="Last Name" value={pax.lastName} onChange={(v) => onChange(idx, "lastName", v)} placeholder="As on ID" required />
        </div>
        <div className="air-grid-3b" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginBottom: 14 }}>
          <Select label="Gender" value={pax.gender} onChange={(v) => onChange(idx, "gender", v)} options={["M", "F"]} required />
          <Field label="Age (years)" type="number" value={pax.age} onChange={(v) => onChange(idx, "age", v)} placeholder="e.g. 28" required />
          <DateInput label="Date of Birth" value={pax.dobInput || ""} required
            onChange={(e) => {
              const val = e.target.value;
              onChange(idx, "dobInput", val);
              onChange(idx, "dob", toDobApi(val));
              if (val) {
                const age = Math.floor((Date.now() - new Date(val)) / (365.25 * 24 * 3600 * 1000));
                onChange(idx, "age", String(age));
              }
            }}
          />
        </div>
        <div className="air-grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 }}>
          <Field label="PAN Card (optional)" value={pax.pancardNumber} onChange={(v) => onChange(idx, "pancardNumber", v)} placeholder="ABCDE1234F" />
          <Field label="Frequent Flyer (optional)" value={pax.frequentFlyer} onChange={(v) => onChange(idx, "frequentFlyer", v)} placeholder="Loyalty number" />
        </div>
      </div>
    </div>
  );
};

// ── Build pax list ────────────────────────────────────────────────
const buildPaxList = ({ adults = 1, children = 0, infants = 0 } = {}) => {
  const list = [];
  let id = 1;
  for (let i = 0; i < adults;   i++) list.push({ paxId: id++, paxType: 0, title: "", firstName: "", lastName: "", gender: "", age: "", dob: "", dobInput: "", pancardNumber: "", frequentFlyer: "" });
  for (let i = 0; i < children; i++) list.push({ paxId: id++, paxType: 1, title: "", firstName: "", lastName: "", gender: "", age: "", dob: "", dobInput: "", pancardNumber: "", frequentFlyer: "" });
  for (let i = 0; i < infants;  i++) list.push({ paxId: id++, paxType: 2, title: "", firstName: "", lastName: "", gender: "", age: "", dob: "", dobInput: "", pancardNumber: "", frequentFlyer: "" });
  return list;
};

// ── Main component ────────────────────────────────────────────────
const FlightBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    searchKey, repricedFlights = [], paxCounts = {},
    tripType = 0, fareChanged = false, searchForm = {},
    bookingSSRDetails: selectedSSRDetailsByLeg = [],
    addonsTotal = 0,
    addonsMeta = [],
    provider = "weflygo", nextraBookingKey = null, nextraTotalPrice = null, nextraDomInt = null,
    nextraSelectedAddons = [],
  } = location.state || {};

  const mid = useSelector((s) => s.auth.employeeDetails?.username);
  const { callApi: tempBookApi, loading: booking } = useApi("/air/temp-booking", "POST");

  const [passengers,    setPassengers]    = useState(() => buildPaxList(paxCounts));
  const [contact,       setContact]       = useState({ mobile: "", email: "" });
  const [gstEnabled,    setGstEnabled]    = useState(false);
  const [gst,           setGst]           = useState({ number: "", holderName: "", address: "" });
  const [error,         setError]         = useState("");
  const [mobFoc,        setMobFoc]        = useState(false);
  const [mailFoc,       setMailFoc]       = useState(false);

  const totalPax = passengers.length;
  // Nextra's flightfare/totalPrice is already the GRAND total for every passenger searched —
  // see the matching note in FlightSSRSeat.js.
  // WeFlyGo's FareDetails is PER PAX TYPE (PAX_Type: 0=Adult/1=Child/2=Infant, per
  // AirFareDetail.java) — each entry is that type's own Total_Amount, not a flat per-pax rate.
  // Summing every entry and multiplying by total headcount double-(or worse-)counts as soon as
  // pax types are mixed (2 adults + 2 children at different fares A/C should total 2A+2C, not
  // (A+C)*4) — each entry is multiplied by ONLY its own type's count instead.
  const fareAmount = provider === "nextra"
    ? (nextraTotalPrice || 0)
    : (() => {
        const countByType = [paxCounts.adults || 0, paxCounts.children || 0, paxCounts.infants || 0];
        return repricedFlights.reduce((sum, f) => {
          const fareDetails = f.Fares?.[0]?.FareDetails || [];
          return sum + fareDetails.reduce((s, fd) => s + (fd.Total_Amount || 0) * (countByType[fd.PAX_Type] ?? 0), 0);
        }, 0);
      })();
  // addonsTotal (seats/SSR picked on the previous step) must be included here —
  // this is what actually gets charged to the merchant wallet at payment time.
  const totalAmount = fareAmount + addonsTotal;

  const updatePax = (idx, field, value) =>
    setPassengers((prev) => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));

  const validate = () => {
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i];
      if (!p.title)            return `Passenger ${i + 1}: select title`;
      if (!p.firstName.trim()) return `Passenger ${i + 1}: enter first name`;
      if (!p.lastName.trim())  return `Passenger ${i + 1}: enter last name`;
      if (!p.gender)           return `Passenger ${i + 1}: select gender`;
      if (!p.dob)              return `Passenger ${i + 1}: enter date of birth`;
    }
    if (!contact.mobile || contact.mobile.length < 10) return "Enter a valid 10-digit mobile number";
    // A weak "has an @" check let malformed emails (e.g. missing a domain suffix) through to
    // WeFlyGo, which rejects the whole TempBooking with an unhelpful generic error instead of
    // pointing at the email — catch it here instead with a clear message.
    if (!contact.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) {
      return "Enter a valid email address (e.g. name@example.com)";
    }
    if (gstEnabled && !gst.number.trim()) return "Enter GST number";
    return null;
  };

  const handleSubmit = async () => {
    setError("");
    const err = validate();
    if (err) return setError(err);

    // Nextra has no TempBooking-equivalent step — its /book call both charges the wallet and
    // confirms the ticket in one shot, and needs the MPIN that's only collected on the next
    // screen. Just carry passenger/contact details forward; the real API call happens there.
    if (provider === "nextra") {
      return navigate("/flight-confirm", {
        state: {
          provider, bookingRefNo: "", repricedFlights, totalAmount, searchKey,
          passengers, contact, searchForm, paxCounts, tripType,
          nextraBookingKey, nextraDomInt, nextraSelectedAddons,
        },
      });
    }

    const paxDetails = passengers.map((p) => ({
      paxId: p.paxId, paxType: p.paxType, title: p.title,
      firstName: p.firstName.trim(), lastName: p.lastName.trim(),
      gender: p.gender === "M" ? 0 : 1,
      age: Number(p.age) || 0, dob: p.dob,
      passportNumber: "", passportIssuingCountry: "", passportExpiry: "",
      nationality: "IN",
      frequentFlyerDetails: p.frequentFlyer ? [{ airlineCode: "", frequentFlyerNumber: p.frequentFlyer }] : [],
      pancardNumber: p.pancardNumber || "",
    }));

    // Each segment gets ONLY its own leg's picks (selectedSSRDetailsByLeg[legIdx]) — previously
    // every segment got the exact same combined list, so onward and return ended up with
    // byte-identical BookingSSRDetails and WeFlyGo rejected ticketing for a duplicated SSR.
    const bookingFlightDetails = repricedFlights.map((f, legIdx) => ({
      searchKey, flightKey: f.Flight_Key,
      fareKey: f.Fares?.[0]?.Fare_Key || null,
      bookingSSRDetails: selectedSSRDetailsByLeg[legIdx] || [],
    }));

    const cancellationType = repricedFlights[0]?.Fares?.[0]?.Refundable === true
      ? "REFUNDABLE" : "NON_CANCELLABLE";

    const flightsMeta = repricedFlights.map((f, i) => ({
      trip_index: i,
      flight_key: f.Flight_Key,
      airline_code: f.Airline_Code,
      flight_numbers: f.Flight_Numbers,
      origin: f.Segments?.[0]?.Origin,
      destination: f.Segments?.[f.Segments?.length - 1]?.Destination,
      departure_dt: f.Segments?.[0]?.Departure_DateTime,
      arrival_dt: f.Segments?.[f.Segments?.length - 1]?.Arrival_DateTime,
      fare_amount: (f.Fares?.[0]?.FareDetails || []).reduce((s, fd) => s + (fd.Total_Amount || 0), 0),
    }));

    const passengersMeta = passengers.map((p) => ({
      pax_id: p.paxId,
      pax_type: p.paxType,
      title: p.title,
      first_name: p.firstName.trim(),
      last_name: p.lastName.trim(),
      gender: p.gender,
      dob: p.dob,
      age: Number(p.age) || 0,
      pan_card: p.pancardNumber || "",
    }));

    try {
      const res = await tempBookApi({
        customerMobile: contact.mobile, passengerMobile: contact.mobile,
        whatsappMobile: contact.mobile, passengerEmail: contact.email,
        paxDetails, gst: gstEnabled,
        gstNumber: gst.number, gstHolderName: gst.holderName, gstAddress: gst.address,
        bookingFlightDetails, costCenterId: "", projectId: "", bookingRemark: "",
        // DB metadata fields
        mid,
        searchKey,
        origin: searchForm.from?.toUpperCase(),
        destination: searchForm.to?.toUpperCase(),
        travelDate: searchForm.travelDate,
        tripType,
        totalAmount,
        cancellationType,
        flightsMeta,
        passengersMeta,
        addonsMeta,
      });

      const bookError = apiErrorMessage(res);
      if (bookError) return setError(bookError);
      if (!res?.Booking_RefNo)
        return setError("Booking failed. Please try again.");

      navigate("/flight-confirm", {
        state: { bookingRefNo: res.Booking_RefNo, repricedFlights, totalAmount, searchKey, passengers, contact, searchForm },
      });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Booking failed. Try again.");
    }
  };

  // Empty state
  if (!repricedFlights.length) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, background: C.bg, fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif" }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: "#EFF8FF", display: "flex", alignItems: "center", justifyContent: "center", color: C.sky }}>
          <IcAirplaneFill size={28} color={C.sky} />
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>No flight selected</div>
        <div style={{ fontSize: 13, color: C.muted }}>Go back and search for a flight first.</div>
        <button onClick={() => navigate("/flight-search")} style={{ background: C.navy, color: "#fff", border: "none", borderRadius: 10, padding: "10px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Search flights
        </button>
      </div>
    );
  }

  const STEPS = [
    { label: "Search",  done: true  },
    { label: "Details", done: false, active: true  },
    { label: "Payment", done: false },
  ];

  return (
    <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif" }}>
      <AirFontImport />

      {/* ── Sticky header ── */}
      <div className="air-booking-header" style={{ background: "#fff", borderBottom: `1px solid ${C.border}`, padding: "10px 20px", display: "flex", alignItems: "center", gap: 14, position: "sticky", top: 0, zIndex: 20 }}>
        <button onClick={() => navigate(-1)} style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${C.border}`, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, flexShrink: 0 }}>
          <IcArrowLeft size={18} />
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, lineHeight: 1.2 }}>Passenger Details</div>
          <div style={{ fontSize: 11, color: C.label, marginTop: 2 }}>
            {searchForm.from} → {searchForm.to} · {totalPax} passenger{totalPax > 1 ? "s" : ""}
          </div>
        </div>

        {/* Step indicator */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.label}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: s.done ? C.green : s.active ? C.sky : C.border, color: "#fff", flexShrink: 0 }}>
                  {s.done
                    ? <IcCheck size={10} strokeWidth="3" />
                    : <span style={{ fontSize: 9, fontWeight: 800 }}>{i + 1}</span>
                  }
                </div>
                <span className="air-step-label" style={{ fontSize: 10, fontWeight: 700, color: s.done ? C.green : s.active ? C.sky : C.label }}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{ width: 16, height: 1.5, background: s.done ? C.green : C.border, borderRadius: 2 }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Page body ── */}
      <div className="air-booking-body" style={{ maxWidth: 780, margin: "0 auto", padding: "20px 16px 130px" }}>

        {/* Fare changed warning */}
        {fareChanged && (
          <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12, padding: "12px 16px", marginBottom: 14, display: "flex", gap: 10, alignItems: "flex-start", color: "#92400E" }}>
            <span style={{ flexShrink: 0, marginTop: 1 }}><IcAlertTri size={15} /></span>
            <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.5 }}>
              Fare updated since search — please review the latest price before continuing.
            </span>
          </div>
        )}

        {/* Boarding-pass cards */}
        {repricedFlights.map((f, i) => (
          <FlightCard key={i} flight={f}
            label={tripType === 1 ? (i === 0 ? "Onward" : "Return") : null}
          />
        ))}

        {/* Contact */}
        <Card icon={<IcPhone size={16} />} title="Contact Information">
          <div className="air-grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 14 }}>
            <div>
              <Label required>Mobile Number</Label>
              <input type="tel" value={contact.mobile} maxLength={10} placeholder="10-digit mobile"
                onChange={(e) => setContact((c) => ({ ...c, mobile: e.target.value.replace(/\D/g, "") }))}
                onFocus={() => setMobFoc(true)} onBlur={() => setMobFoc(false)}
                style={iStyle(mobFoc)} />
            </div>
            <div>
              <Label required>Email Address</Label>
              <input type="email" value={contact.email} placeholder="passenger@email.com"
                onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                onFocus={() => setMailFoc(true)} onBlur={() => setMailFoc(false)}
                style={iStyle(mailFoc)} />
            </div>
          </div>
        </Card>

        {/* Travellers */}
        <div style={{ marginBottom: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: "#EFF8FF", display: "flex", alignItems: "center", justifyContent: "center", color: C.sky }}>
              <IcUsers size={16} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>Traveller Details</span>
          </div>
          {passengers.map((pax, idx) => (
            <PaxCard key={idx} pax={pax} idx={idx} onChange={updatePax} />
          ))}
        </div>

        {/* GST */}
        <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: "16px 20px", marginBottom: 12 }}>
          <div onClick={() => setGstEnabled((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none", marginBottom: gstEnabled ? 18 : 0 }}>
            <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${gstEnabled ? C.sky : C.border}`, background: gstEnabled ? C.sky : "#fff", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0, transition: "all 0.15s" }}>
              {gstEnabled && <IcCheck size={11} />}
            </div>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: "#EFF8FF", display: "flex", alignItems: "center", justifyContent: "center", color: C.sky, flexShrink: 0 }}>
              <IcBriefcase size={15} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.navy }}>Add GST details</div>
              <div style={{ fontSize: 11, color: C.label, marginTop: 1 }}>For business travel reimbursement</div>
            </div>
          </div>
          {gstEnabled && (
            <div className="air-grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 }}>
              <Field label="GST Number" value={gst.number} onChange={(v) => setGst((g) => ({ ...g, number: v }))} placeholder="22AAAAA0000A1Z5" required />
              <Field label="Company Name" value={gst.holderName} onChange={(v) => setGst((g) => ({ ...g, holderName: v }))} placeholder="As per GST registration" />
              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Registered Address" value={gst.address} onChange={(v) => setGst((g) => ({ ...g, address: v }))} placeholder="Company address" />
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: "12px 16px", display: "flex", gap: 8, alignItems: "center", color: C.red, marginBottom: 12 }}>
            <IcXCircle size={15} />
            <span style={{ fontSize: 13, fontWeight: 500 }}>{error}</span>
          </div>
        )}

        <div style={{ textAlign: "center" }}>
          <button
            onClick={() => navigate("/flight-history")}
            style={{ background: "none", border: "none", color: C.sky, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "8px 0" }}
          >
            View Booking History →
          </button>
        </div>
      </div>

      {/* ── Sticky footer ── */}
      <div className="air-booking-footer" style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "#fff", borderTop: `1px solid ${C.border}`, boxShadow: "0 -4px 20px rgba(0,0,0,0.07)", padding: "12px 20px", zIndex: 20 }}>
        <div style={{ maxWidth: 780, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, color: C.label, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Total · {totalPax} passenger{totalPax > 1 ? "s" : ""}
            </div>
            <div style={{ lineHeight: 1.15 }}>
              <Readout size={26} weight={700} color={C.navy} tracking="-0.01em">₹{totalAmount.toLocaleString("en-IN")}</Readout>
            </div>
            <div style={{ fontSize: 10, color: C.label, marginTop: 2 }}>
              Incl. taxes &amp; fees{addonsTotal > 0 ? ` · incl. ₹${addonsTotal.toLocaleString("en-IN")} add-ons` : ""}
            </div>
          </div>
          <button
            className="air-booking-cta"
            onClick={handleSubmit}
            disabled={booking}
            style={{
              background: booking ? C.label : C.sky,
              color: "#fff", border: "none", borderRadius: 14,
              padding: "13px 28px", fontSize: 14, fontWeight: 700,
              cursor: booking ? "not-allowed" : "pointer",
              boxShadow: booking ? "none" : "0 4px 16px rgba(14,165,233,0.38)",
              display: "flex", alignItems: "center", gap: 8,
              transition: "background 0.2s, box-shadow 0.2s",
              whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            {booking ? (
              <>
                <span style={{ width: 15, height: 15, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                Reserving seat…
              </>
            ) : (
              <>
                Continue to payment
                <IcAirplaneFill size={15} color="#fff" />
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 480px) {
          .air-grid-3a { grid-template-columns: 90px minmax(0,1fr) !important; }
          .air-grid-3a > div:last-child { grid-column: 1 / -1; }
          .air-grid-3b { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
          .air-grid-3b > div:last-child { grid-column: 1 / -1; }
          .air-grid-2 { grid-template-columns: 1fr !important; }
          .air-booking-header { padding: 8px 12px !important; gap: 8px !important; }
          .air-step-label { display: none; }
          .air-booking-body { padding: 16px 12px 130px !important; }
          .air-booking-footer { padding: 10px 12px !important; }
          .air-booking-cta { padding: 12px 18px !important; font-size: 13px !important; }
          .air-route-code span { font-size: 28px !important; }
        }
      `}</style>
    </div>
  );
};

export default FlightBooking;
